
import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schemaData?: object;
  pageType?: 'WebPage' | 'Service' | 'LocalBusiness';
}

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical, 
  schemaData,
  pageType = 'WebPage'
}: SEOHeadProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta description
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
    }

    // Update meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // Update canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Update last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Add Schema.org JSON-LD
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hig-construcao-servicos.pt';
    
    let schema;
    if (schemaData) {
      schema = schemaData;
    } else if (pageType === 'LocalBusiness') {
      schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "HIG Construção e Serviços",
        "description": "Empresa especializada em remodelações de casas em Braga, Portugal",
        "url": siteUrl,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Avenida Dom Pedro Nunes 587",
          "addressLocality": "Vila Verde, Braga",
          "addressCountry": "PT"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 41.5518,
          "longitude": -8.4229
        },
        "telephone": "+351936602153",
        "email": "hanielany@gmail.com",
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 41.5518,
            "longitude": -8.4229
          },
          "geoRadius": "50000"
        },
        "openingHours": "Mo-Fr 08:00-18:00, Sa 09:00-13:00"
      };
    } else if (pageType === 'Service') {
      schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Serviços de Remodelação HIG",
        "description": "Serviços completos de remodelação de casas em Braga",
        "provider": {
          "@type": "LocalBusiness",
          "name": "HIG Construção e Serviços",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Avenida Dom Pedro Nunes 587",
            "addressLocality": "Vila Verde, Braga",
            "addressCountry": "PT"
          },
          "telephone": "+351936602153"
        },
        "areaServed": "Braga, Portugal",
        "serviceType": "Remodelação de Casas"
      };
    } else {
      schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": title || "HIG Construção e Serviços",
        "description": description || "Empresa especializada em remodelações de casas em Braga",
        "url": canonical || siteUrl,
        "mainEntity": {
          "@type": "LocalBusiness",
          "name": "HIG Construção e Serviços",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Avenida Dom Pedro Nunes 587",
            "addressLocality": "Vila Verde, Braga",
            "addressCountry": "PT"
          }
        }
      };
    }

    // Remove existing schema script
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema script
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

  }, [title, description, keywords, canonical, schemaData, pageType]);

  return null;
};

export default SEOHead;
